<template>
  <div
    v-if="visivel"
    class="prod-pesq-overlay"
    @keydown.esc.stop.prevent="fechar"
  >
    <div class="prod-pesq-modal">
      <header class="prod-pesq-modal__header">
        <h3 class="prod-pesq-modal__title">
          <i class="fas fa-search"></i> Pesquisa avançada de produtos
        </h3>
        <button class="prod-pesq-modal__close" @click="fechar">×</button>
      </header>

      <div class="prod-pesq-modal__filters">
        <input
          ref="termoInput"
          v-model="termo"
          type="text"
          class="prod-pesq-modal__input"
          placeholder="Descrição, código, código de barras ou referência..."
          @input="onBuscar"
          @keydown.enter.prevent="onEnter"
          @keydown.down.prevent="navegar(1)"
          @keydown.up.prevent="navegar(-1)"
        />
        <select
          v-if="grupos.length"
          v-model="grupoSelecionado"
          class="prod-pesq-modal__input"
          style="margin-top:6px"
          @change="onBuscarFiltrado"
        >
          <option :value="null">Todos os grupos</option>
          <option v-for="g in grupos" :key="g.id_grupo" :value="g.id_grupo">
            {{ g.descricao }}
          </option>
        </select>
      </div>

      <div class="prod-pesq-modal__body">
        <div v-if="buscando" class="prod-pesq-modal__loading">
          <i class="fas fa-spinner fa-spin"></i> Buscando...
        </div>
        <div v-else-if="!resultados.length && termo.length >= 1" class="prod-pesq-modal__vazio">
          <i class="fas fa-search-minus"></i> Nenhum produto encontrado.
        </div>
        <div v-else-if="!resultados.length" class="prod-pesq-modal__hint">
          Digite para buscar produtos.
        </div>

        <table v-else class="prod-pesq-modal__tabela">
          <thead>
            <tr>
              <th>Código</th>
              <th>Descrição</th>
              <th>Cód. Barras</th>
              <th>Referência</th>
              <th class="text-right">Preço</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(p, idx) in resultados"
              :key="(p.id_produto || p.id) + '-' + idx"
              ref="resultadoRows"
              :class="{ 'prod-pesq-modal__row--ativa': idx === selectedIdx }"
              :aria-selected="idx === selectedIdx ? 'true' : 'false'"
              @mouseenter="selectedIdx = idx"
              @click="confirmar(p)"
              @dblclick="confirmar(p)"
            >
              <td>{{ p.codigo || '—' }}</td>
              <td>{{ p.descricao }}</td>
              <td>{{ p.cod_barras || '—' }}</td>
              <td>{{ referenciaProduto(p) || '—' }}</td>
              <td class="text-right">{{ formatBRL(p.valor_venda || p.preco || p.valor || 0) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <footer class="prod-pesq-modal__footer">
        <span class="prod-pesq-modal__hint-foot">
          <i class="fas fa-keyboard"></i> Use ↑ ↓ para navegar · Enter para selecionar · ESC para fechar
        </span>
        <button class="prod-pesq-modal__btn prod-pesq-modal__btn--cancel" @click="fechar">
          Fechar
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
import _ from "lodash";
import ProdutoLocalRepository from "@/modules/PdvDesktop/repositories/ProdutoLocalRepository";
import GrupoLocalRepository from "@/modules/PdvDesktop/repositories/GrupoLocalRepository";

export default {
  name: "ProdutoPesquisaAvancadaModal",

  props: {
    visivel: { type: Boolean, default: false },
  },

  data() {
    return {
      termo: "",
      resultados: [],
      buscando: false,
      selectedIdx: 0,
      grupos: [],
      grupoSelecionado: null,
    };
  },

  created() {
    this.onBuscar = _.debounce(this._buscar, 300);
  },

  watch: {
    visivel(v) {
      if (v) {
        this.termo = "";
        this.resultados = [];
        this.selectedIdx = 0;
        this.grupoSelecionado = null;
        GrupoLocalRepository.listar().then((g) => { this.grupos = g || []; }).catch(() => {});
        this.$nextTick(() => this.$refs.termoInput && this.$refs.termoInput.focus());
      }
    },
  },

  methods: {
    formatBRL(val) {
      return Number(val || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },

    referenciaProduto(produto) {
      return produto?.referencia ??
        produto?.codigo_referencia ??
        produto?.referencia_produto ??
        produto?.ref ??
        "";
    },

    async _buscar() {
      const t = (this.termo || "").trim();
      if (t.length < 1) {
        this.resultados = [];
        this.selectedIdx = 0;
        return;
      }
      this.buscando = true;
      try {
        // Tentativa por código de barras exato quando totalmente numérico
        let lista = [];
        if (/^\d{4,}$/.test(t)) {
          try {
            const exato = await ProdutoLocalRepository.buscarPorCodigoBarras(t);
            if (exato) lista.push(exato);
          } catch (e) { /* ignora */ }
        }
        const search = (await ProdutoLocalRepository.search(t, this.grupoSelecionado ?? null, {
          typeFilter: 3,
          pesquisaAvancada: true,
        })) || [];
        // Dedup pelo id_produto/id
        const map = new Map();
        for (const p of [...lista, ...search]) {
          const k = p.id_produto || p.id || p.codigo;
          if (!map.has(k)) map.set(k, p);
        }
        this.resultados = Array.from(map.values());
        this.selectedIdx = 0;
      } catch (e) {
        console.error("[ProdutoPesquisaAvancadaModal] erro:", e);
        this.resultados = [];
      } finally {
        this.buscando = false;
      }
    },

    onBuscarFiltrado() {
      this.onBuscar();
    },

    navegar(delta) {
      if (!this.resultados.length) return;
      const n = this.resultados.length;
      this.selectedIdx = (this.selectedIdx + delta + n) % n;
      this.$nextTick(() => {
        const rows = this.$refs.resultadoRows || [];
        const row = Array.isArray(rows) ? rows[this.selectedIdx] : rows;
        if (row && typeof row.scrollIntoView === "function") {
          row.scrollIntoView({ block: "nearest" });
        }
      });
    },

    onEnter() {
      if (!this.resultados.length) return;
      const p = this.resultados[this.selectedIdx] || this.resultados[0];
      if (p) this.confirmar(p);
    },

    confirmar(produto) {
      this.$emit("selecionar", produto);
    },

    fechar() {
      this.$emit("fechar");
    },
  },
};
</script>

<style scoped>
.prod-pesq-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.55);
  display: flex; align-items: center; justify-content: center;
  z-index: 4100;
}
.prod-pesq-modal {
  background: #fff; border-radius: 8px;
  width: 100%; max-width: 920px; max-height: 86vh;
  box-shadow: 0 12px 32px rgba(0,0,0,0.3);
  display: flex; flex-direction: column; overflow: hidden;
}
.prod-pesq-modal__header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 14px 20px; border-bottom: 1px solid #e5e7eb;
}
.prod-pesq-modal__title { margin: 0; font-size: 17px; color: #1f2937; }
.prod-pesq-modal__title i { color: var(--primary-color, #ee7361); margin-right: 6px; }
.prod-pesq-modal__close {
  background: transparent; border: 0; font-size: 24px; cursor: pointer; color: #6b7280;
}
.prod-pesq-modal__filters { padding: 12px 20px; border-bottom: 1px solid #f1f5f9; }
.prod-pesq-modal__input {
  width: 100%; padding: 10px 12px; border: 1px solid #cbd5e1; border-radius: 6px; font-size: 15px;
}
.prod-pesq-modal__body { padding: 0; flex: 1; overflow: auto; min-height: 220px; }
.prod-pesq-modal__loading,
.prod-pesq-modal__vazio,
.prod-pesq-modal__hint {
  text-align: center; padding: 30px 20px; color: #64748b;
}
.prod-pesq-modal__tabela { width: 100%; border-collapse: collapse; font-size: 15px; }
.prod-pesq-modal__tabela thead th {
  background: #f8fafc; text-align: left; padding: 8px 12px;
  border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #475569;
  position: sticky; top: 0;
}
.prod-pesq-modal__tabela tbody td {
  padding: 8px 12px; border-bottom: 1px solid #f1f5f9; color: #1f2937;
}
.prod-pesq-modal__tabela tbody tr { cursor: pointer; }
.prod-pesq-modal__tabela tbody tr:hover:not(.prod-pesq-modal__row--ativa) td {
  background: #eff6ff;
}
.prod-pesq-modal__tabela tbody .prod-pesq-modal__row--ativa td {
  background: #2563eb;
  border-bottom-color: #1d4ed8;
  color: #fff;
  font-weight: 700;
}
.prod-pesq-modal__tabela tbody .prod-pesq-modal__row--ativa td:first-child {
  box-shadow: inset 5px 0 0 #1e3a8a;
}
.text-right { text-align: right; }
.prod-pesq-modal__footer {
  display: flex; justify-content: space-between; align-items: center;
  padding: 12px 20px; background: #f9fafb; border-top: 1px solid #e5e7eb;
}
.prod-pesq-modal__hint-foot { font-size: 14px; color: #6b7280; }
.prod-pesq-modal__btn {
  padding: 8px 16px; border-radius: 6px; border: 0; cursor: pointer; font-weight: 500;
}
.prod-pesq-modal__btn--cancel { background: #e5e7eb; color: #374151; }
.prod-pesq-modal__btn--cancel:hover { background: #d1d5db; }
</style>
